import React from 'react';
import { motion } from 'framer-motion';

const WhatsAppBot = () => {
  const Section = ({ bg, children }) => (
    <section className={`py-16 px-6 ${bg || ''}`}>{children}</section>
  );

  const Feature = ({ icon, title, desc }) => (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
      <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center mx-auto mb-4 text-2xl">{icon}</div>
      <h3 className="text-lg font-bold text-gray-800 mb-2">{title}</h3>
      <p className="text-sm text-gray-500">{desc}</p>
    </div>
  );

  const ProblemCard = ({ icon, title, desc }) => (
    <div className="bg-white rounded-xl p-6 border-l-4 border-red-500">
      <div className="text-3xl mb-2">{icon}</div>
      <h3 className="text-lg font-bold text-red-500 mb-1">{title}</h3>
      <p className="text-sm text-gray-500">{desc}</p>
    </div>
  );

  const Step = ({ num, title, desc }) => (
    <div className="flex gap-4 mb-8">
      <div className="w-11 h-11 bg-green-600 text-white rounded-full flex items-center justify-center font-extrabold text-lg flex-shrink-0">{num}</div>
      <div>
        <h3 className="text-lg font-bold text-white">{title}</h3>
        <p className="text-sm text-blue-200">{desc}</p>
      </div>
    </div>
  );

  const Plan = ({ name, price, desc, features, popular }) => (
    <div className={`bg-white rounded-2xl p-7 border-2 relative ${popular ? 'border-blue-600' : 'border-gray-200'}`}>
      {popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-extrabold px-3 py-1 rounded-lg tracking-wide">MAIS POPULAR</div>}
      <div className="text-sm font-bold text-gray-400 uppercase tracking-wide">{name}</div>
      <div className="text-4xl font-black text-gray-800 mt-2 mb-1">{price}<span className="text-base font-medium text-gray-400">/mes</span></div>
      <div className="text-sm text-gray-400 mb-5 pb-5 border-b border-gray-200">{desc}</div>
      <ul className="space-y-2">
        {features.map((f, i) => <li key={i} className="text-sm pl-6 relative before:content-['✓'] before:absolute before:left-0 before:text-blue-600 before:font-bold">{f}</li>)}
      </ul>
    </div>
  );

  return (
    <div className="pt-16">
      {/* HERO */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="bg-gradient-to-br from-gray-900 via-blue-900 to-blue-700 text-white py-20 px-6 text-center relative overflow-hidden"
      >
        <div className="relative z-10 max-w-3xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-blue-300 mb-6">Crespo Cyber Consulting LLC</p>
          <h1 className="text-4xl md:text-5xl font-black leading-tight mb-5">
            Seu negocio atendendo clientes <span className="text-green-400">24 horas por dia</span> no WhatsApp
          </h1>
          <p className="text-lg text-blue-100 mb-10 max-w-xl mx-auto">
            Nunca mais perca um cliente por falta de resposta. Nosso assistente virtual cuida do atendimento, agendamento e lembretes automaticamente.
          </p>
          <div className="flex justify-center gap-10 flex-wrap">
            <div className="text-center"><span className="block text-3xl font-black text-green-400">98%</span><span className="text-xs text-blue-200 uppercase tracking-wide">dos brasileiros usam WhatsApp</span></div>
            <div className="text-center"><span className="block text-3xl font-black text-green-400">60%</span><span className="text-xs text-blue-200 uppercase tracking-wide">menos faltas com lembretes</span></div>
            <div className="text-center"><span className="block text-3xl font-black text-green-400">24/7</span><span className="text-xs text-blue-200 uppercase tracking-wide">atendimento sem parar</span></div>
          </div>
        </div>
      </motion.section>

      {/* PROBLEM */}
      <Section bg="bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-extrabold text-center mb-3">Voce reconhece esses problemas?</h2>
          <p className="text-center text-gray-500 mb-10">A maioria dos negocios perde dinheiro todos os dias por causa disso.</p>
          <div className="grid md:grid-cols-3 gap-5">
            <ProblemCard icon="📱" title="Mensagens sem resposta" desc="Seus clientes mandam WhatsApp fora do horario e nunca recebem resposta. Resultado? Vao para o concorrente." />
            <ProblemCard icon="📅" title="Faltas e no-shows" desc="Sem lembretes automaticos, 20-30% dos seus clientes esquecem ou faltam. Voce perde tempo e receita." />
            <ProblemCard icon="⏰" title="Equipe sobrecarregada" desc="Sua equipe gasta horas respondendo as mesmas perguntas: horarios, precos, endereco, disponibilidade." />
          </div>
        </div>
      </Section>

      {/* SOLUTION */}
      <Section>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-extrabold text-center mb-3">O que fazemos por voce</h2>
          <p className="text-center text-gray-500 mb-10">Um assistente virtual que trabalha 24 horas no seu WhatsApp Business.</p>
          <div className="grid md:grid-cols-3 gap-5">
            <Feature icon="🤖" title="Atendimento Inteligente" desc="Responde automaticamente sobre horarios, precos, servicos e localizacao — como se fosse voce." />
            <Feature icon="📅" title="Agendamento pelo WhatsApp" desc="Seu cliente marca consulta direto na conversa. Sem ligacao, sem espera, integrado com seu calendario." />
            <Feature icon="🔔" title="Lembretes que Funcionam" desc="Lembrete automatico 24h e 1h antes. Seus clientes confirmam ou reagendam sem voce precisar ligar." />
            <Feature icon="💳" title="Cobranças com PIX" desc="Envie links de pagamento via WhatsApp. O cliente paga antes de chegar. Menos calote, mais fluxo." />
            <Feature icon="📊" title="Tudo Organizado" desc="Painel com todos os agendamentos, conversas e historico de cada cliente em um so lugar." />
            <Feature icon="🇧🇷" title="100% em Portugues" desc="Bot treinado para o jeito brasileiro de se comunicar. Linguagem natural, nao robotica." />
          </div>
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <section className="py-16 px-6 bg-gradient-to-br from-gray-900 to-blue-900 text-white">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-extrabold text-center mb-3">Como comecar</h2>
          <p className="text-center text-blue-300 mb-10">Voce nao precisa entender de tecnologia. Nos cuidamos de tudo.</p>
          <Step num="1" title="Conversa Inicial (Gratis)" desc="Nos conta como funciona seu atendimento hoje. Entendemos seu negocio e o que voce precisa." />
          <Step num="2" title="Montamos Tudo Para Voce" desc="Configuramos o bot com a cara da sua marca, integramos seu calendario e meios de pagamento." />
          <Step num="3" title="Voce Testa e Aprova" desc="Antes de ativar, voce testa tudo. Ajustamos quantas vezes for preciso ate ficar perfeito." />
          <Step num="4" title="Ativamos e Acompanhamos" desc="Ligamos no seu WhatsApp Business e voce comeca a atender clientes automaticamente. Suporte continuo incluso." />
        </div>
      </section>

      {/* PRICING */}
      <Section bg="bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-extrabold text-center mb-3">Investimento</h2>
          <p className="text-center text-gray-500 mb-10">Planos que cabem no orcamento e geram retorno desde o primeiro mes.</p>
          <div className="grid md:grid-cols-3 gap-5">
            <Plan name="Essencial" price="R$800" desc="Ideal para quem esta comecando a automatizar." features={['Bot WhatsApp personalizado', 'Agendamento online integrado', 'Lembretes automaticos', 'Respostas a perguntas frequentes', 'Suporte por email']} />
            <Plan name="Profissional" price="R$1.500" desc="Para quem quer o pacote completo." popular features={['Tudo do plano Essencial', 'Links de pagamento com PIX', 'CRM de clientes integrado', 'Relatorios e metricas', 'Bot bilingue (PT/EN)', 'Suporte prioritario via WhatsApp']} />
            <Plan name="Premium" price="R$3.000" desc="Para clinicas e negocios de alto volume." features={['Tudo do plano Profissional', 'Assistente de voz com IA', 'Multiplos numeros/atendentes', 'Integracoes sob medida', 'Gerente de conta dedicado', 'SLA de resposta em 2 horas']} />
          </div>
        </div>
      </Section>

      {/* ROI */}
      <Section>
        <div className="max-w-2xl mx-auto bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-600 rounded-2xl p-10 text-center">
          <h2 className="text-2xl font-extrabold text-blue-700 mb-4">O investimento se paga rapido</h2>
          <p className="text-gray-500 mb-6">Se voce recuperar apenas 3 clientes por mes que iam desistir, o servico ja se paga.</p>
          <div className="flex items-center justify-center gap-6 flex-wrap">
            <div><span className="block text-3xl font-black text-red-500">R$3.000+</span><span className="text-xs text-gray-400">Perdido por mes em<br/>clientes sem resposta</span></div>
            <span className="text-2xl text-blue-600 font-bold">→</span>
            <div><span className="block text-3xl font-black text-green-600">R$800</span><span className="text-xs text-gray-400">Seu investimento<br/>no plano Essencial</span></div>
          </div>
          <p className="mt-6 text-lg font-bold text-blue-700">Retorno de 275% ja no primeiro mes</p>
        </div>
      </Section>

      {/* CTA */}
      <section className="py-16 px-6 bg-gradient-to-br from-gray-900 to-blue-700 text-white text-center">
        <h2 className="text-3xl font-extrabold mb-4">Vamos conversar sobre seu negocio?</h2>
        <p className="text-blue-200 mb-8 max-w-md mx-auto">Agende uma demonstracao gratuita. Sem compromisso, sem burocracia.</p>
        <a
          href="https://wa.me/17868630388?text=Oi!%20Quero%20saber%20mais%20sobre%20o%20bot%20de%20WhatsApp%20para%20meu%20negocio."
          className="inline-block bg-green-600 hover:bg-green-700 text-white font-extrabold text-lg px-10 py-4 rounded-xl transition-transform hover:scale-105 shadow-lg"
          target="_blank"
          rel="noopener noreferrer"
        >
          FALAR NO WHATSAPP
        </a>
        <p className="mt-5 text-sm text-blue-300">info@crespocyberconsulting.com | +1 (786) 863-0388</p>
      </section>
    </div>
  );
};

export default WhatsAppBot;
